import React, { useState } from 'react';
import { useModal } from '@europrocurement/flexy-components';
import { AxiosResponse } from 'axios';

import { FproDosDocFileJsonldDocumentfileRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useSnackbar } from 'notistack';

import {
    ADD_DOS_DOC_CTX,
    UPDATE_DOS_DOC_CTX,
    useDossierDocumentsCtx,
    useDossierDocumentsCtxDispatch,
} from '@europrocurement/l2d-modules/providers';
import LiasseCheckerForm from '../components/shared/LiasseCheckerForm/LiasseCheckerForm';
import { CreatedLiasseType } from '../providers/types';
import useDossierDocumentFilesApi from './useDossierDocumentFilesApi';

const useFileChecker = () => {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const { modalActions } = useModal();

    const documentContext = useDossierDocumentsCtx();
    const { removeDosDoc, removeDosDocFile } = useDossierDocumentFilesApi();
    const dossierDocumentsDispatch = useDossierDocumentsCtxDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const handleSubmit = async (
        filesToRemove: FproDosDocFileJsonldDocumentfileRead[],
        allnewFiles: FproDosDocFileJsonldDocumentfileRead[],
    ) => {
        if (isSubmitting) {
            return;
        }
        if (filesToRemove.length > 0) {
            setIsSubmitting(true);

            const validHttpCodes = [200, 204];
            const errors: FproDosDocFileJsonldDocumentfileRead[] = [];

            // Delete FproDosDoc
            filesToRemove.map(async (file) => {
                if (file.id && file.typeDocDossier?.id) {
                    // Delete DosDocFile
                    const fichierDelRepsonse = await removeDosDocFile(file.id);

                    if (validHttpCodes.includes(fichierDelRepsonse.status)) {
                        // Delete DosDoc
                        const docDelRepsonse = await removeDosDoc(file.typeDocDossier.id);

                        if (!validHttpCodes.includes(docDelRepsonse.status)) {
                            errors.push(file);
                        }
                    } else {
                        errors.push(file);
                    }
                } else {
                    errors.push(file);
                }
                return file;
            });

            const filesToKeep = allnewFiles.filter(
                (file) => !filesToRemove.some((fileToremove) => fileToremove.id === file.id),
            );

            // Update documents context
            // DosDoc and dosDocFiles rebuild from dosDocFile response
            // The filesResponse may have created a new dosDoc on file creation or set the file in an existing dosDoc
            filesToKeep.map((newFile) => {
                // check if doc is allready existing
                const existingDosDoc = documentContext.dosDocs?.find(
                    (dosDoc) => newFile.typeDocDossier && dosDoc.id === newFile.typeDocDossier.id,
                );
                if (existingDosDoc) {
                    return dossierDocumentsDispatch({
                        type: UPDATE_DOS_DOC_CTX,
                        payload: {
                            ...newFile.typeDocDossier,
                            dossier: newFile.dossier,
                            statut: newFile.statut,
                            fichiers: [newFile],
                        },
                    });
                }
                return dossierDocumentsDispatch({
                    type: ADD_DOS_DOC_CTX,
                    payload: {
                        ...newFile.typeDocDossier,
                        dossier: newFile.dossier,
                        statut: newFile.statut,
                        fichiers: [newFile],
                    },
                });
            });

            if (errors.length > 0) {
                errors.map((errorFile) =>
                    enqueueSnackbar(
                        `Une erreur est survenue lors de la suppression du fichier ${errorFile.fichier}`,
                        {
                            variant: 'error',
                        },
                    ),
                );
            }

            setIsSubmitting(false);
            modalActions.close();
        } else {
            setIsSubmitting(false);
            modalActions.close();
        }
    };

    const displayFilesCheckerForm = (
        createdFiles: CreatedLiasseType[],
        getFichierContent: (fichierId: number) => Promise<AxiosResponse<void>>,
    ) => {
        // Modal de vérification de fichiers
        modalActions.call(
            <LiasseCheckerForm
                createdLiasses={createdFiles}
                getStatusIdentifier={documentContext.getStatusIdentifier}
                getFichierContent={getFichierContent}
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
            />,
            false,
            true,
            () => {
                createdFiles.map((file) =>
                    file.createdFiles.map((newFile) => {
                        // check if doc is allready existing
                        const existingDosDoc = documentContext.dosDocs?.find(
                            (dosDoc) =>
                                newFile.typeDocDossier && dosDoc.id === newFile.typeDocDossier.id,
                        );
                        if (existingDosDoc) {
                            return dossierDocumentsDispatch({
                                type: UPDATE_DOS_DOC_CTX,
                                payload: {
                                    ...newFile.typeDocDossier,
                                    dossier: newFile.dossier,
                                    statut: newFile.statut,
                                    fichiers: [newFile],
                                },
                            });
                        }
                        return dossierDocumentsDispatch({
                            type: ADD_DOS_DOC_CTX,
                            payload: {
                                ...newFile.typeDocDossier,
                                dossier: newFile.dossier,
                                statut: newFile.statut,
                                fichiers: [newFile],
                            },
                        });
                    }),
                );
            },
        );
    };

    return { displayFilesCheckerForm };
};

export default useFileChecker;
